import React from "react";
import { DateTime } from "luxon";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { BetSlipShare } from "@models";
import { Button } from "@components/buttons";
import { useRootAppStore } from "@hooks";
import { THEME_SETTINGS } from "@theme-settings";
import { getCurrentCulture } from "@utils";
import { SharedBetSlipActionsTypeEnum } from "@data-types/analytics-tracker/QuickPlayInteractionTypeEnum";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import BetOfferItem from "@features/my-bets/desktop/bet-slip-shared-page/body/item/offer/BetOfferItem";
import { MyBetsStoreContext } from "@state/context/my-bets/MyBetsStoreContext";
import { analyticsTracker } from "@utils/analytics";
import { useFixBody } from "@state/hooks/shared";

export default observer(function ChatBetSlipSharedPopup() {
	const { chatViewStore } = useRootAppStore();

	useFixBody(true, !!chatViewStore.chatBetSlipShareData);

	if (!THEME_SETTINGS.features.isBetSlipShareEnabled) {
		return null;
	}

	if (!chatViewStore.chatBetSlipShareData) {
		return null;
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/ChatBetSlipSharedPopup.tsx"}
		>
			<div className="popup popup--full popup--shared">
				<PopupContent bet={chatViewStore.chatBetSlipShareData} />
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

const PopupContent = observer(function PopupContent(props: {
	bet: BetSlipShare;
}) {
	const { chatViewStore } = useRootAppStore();
	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/ChatBetSlipSharedPopup.tsx/PopupContent"}
		>
			<MyBetsStoreContext.Provider
				value={
					chatViewStore.chatBetSlipShareHandler.BetSlipShareDetails
				}
			>
				<div className="popup__card popup--most__card">
					<PopupHeader
						onClose={
							chatViewStore.chatBetSlipShareHandler.onDispose
						}
					/>
					<PopupBody bet={props.bet} />
					<PopupFooter bet={props.bet} />
				</div>
			</MyBetsStoreContext.Provider>
		</ErrorBoundaryWithPropagation>
	);
});

export const PopupHeader = observer(function PopupHeader(props: {
	onClose: () => void;
}) {
	const { t } = useTranslation("mybets");

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/ChatBetSlipSharedPopup.tsx/PopupHeader"}
		>
			<div className="popup__header">
				<div className="popup__title">{t("SHARE.TITLE")}</div>
				<button
					type="button"
					className="popup__close btn btn--square btn--square--xsml btn--primary--inverted btn--icon"
					onClick={props.onClose}
				>
					<i className="u-icon u-icon--tny u-icon--close" />
				</button>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

export const PopupBody = observer(function PopupBody(props: {
	bet: BetSlipShare;
}) {
	const { t } = useTranslation("mybets");

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/ChatBetSlipSharedPopup.tsx/PopupBody"}
		>
			<div className="popup__body">
				<div className="bet__header__inner bet--slip__event__header__inner">
					<div className="bet__header__col">
						<span className="u-type--xxsml u-color--text--tertiary">
							{t("BET_SLIP.DATE")}:{" "}
						</span>

						<span className="u-type--sml u-type--wgt--semibold u-color--text--primary">
							{DateTime.fromISO(
								props.bet.dateCreated
							).toLocaleString(DateTime.DATETIME_SHORT)}
						</span>
					</div>

					<div className="bet__header__col">
						<span className="u-type--xxsml u-color--text--tertiary">
							{t("BET_SLIP.MAX_COEF")}:{" "}
						</span>

						<span className="u-type--sml u-type--wgt--semibold u-color--text--primary">
							{props.bet.maxCoefficient.toFixed(2)}
						</span>
					</div>
				</div>

				<div className="card--bet">
					<table
						className="table--bets table--slip"
						cellSpacing={0}
						cellPadding={0}
					>
						<thead className="table--bets__head">
							<tr className="table--bets__subhead">
								<th className="table--bets__ts table--bets__ts--combi">
									&nbsp;
								</th>
								<th className="table--bets__ts table--bets__ts--event">
									{t<string>("mybets:ITEM.EVENT_NAME_COLUMN")}
								</th>
								<th className="table--bets__ts table--bets__ts--bank">
									&nbsp;
								</th>
								<th className="table--bets__ts table--bets__ts--bet-type">
									{t<string>("mybets:ITEM.TIP_NAME_COLUMN")}
								</th>
								<th className="table--bets__ts table--bets__ts--odds">
									{t<string>("mybets:ITEM.QUOTA_COLUMN")}
								</th>
								<th className="table--bets__ts table--bets__ts--results">
									{t<string>("mybets:ITEM.RESULT_COLUMN")}
								</th>
								<th className="table--bets__ts table--bets__ts--icon">
									&nbsp;
								</th>
							</tr>
						</thead>
						<tbody className="table--bets__body">
							{props.bet.betSlipOffers.map((offer) => (
								<BetOfferItem key={offer.id} offer={offer} />
							))}
						</tbody>
					</table>
				</div>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

const PopupFooter = observer(function PopupFooter(props: {
	bet: BetSlipShare;
}) {
	const { chatViewStore } = useRootAppStore();
	const { t } = useTranslation();
	const { bet } = props;

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/ChatBetSlipSharedPopup.tsx/PopupFooter"}
		>
			<div className="popup__footer">
				<div className="popup__footer__inner">
					<Button
						className={`btn--base btn--primary--inverted`}
						onClick={() => {
							analyticsTracker.trackQuickPlayInteraction(
								SharedBetSlipActionsTypeEnum.PageVisit
							);
							chatViewStore.chatBetSlipShareHandler.onDispose();
							chatViewStore.toggleChatBox(false);
							App.state.redirect(
								`/${getCurrentCulture()}/quick-play/shared-bet-slips`
							);
						}}
						type="button"
						btnText={t("mybets:SHARE.SEE_ALL")}
					/>
				</div>
				<Button
					className="btn btn--base btn--primary"
					onClick={() =>
						chatViewStore.chatBetSlipShareHandler.addToSlip(bet)
					}
					type="button"
					iconClassName="u-icon u-icon--sml u-icon--refresh u-color--core--snow"
					btnText={t("mybets:SHARE.ADD_TO_SLIP")}
				/>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});
